import React, { useState, useEffect, useMemo } from 'react';
import { db, auth, getCollectionName } from './firebase';
import { collection, query, where, onSnapshot, addDoc, doc, updateDoc, deleteDoc, orderBy, getDocs, writeBatch } from './firestore-compat';
import { toTitleCase } from './utils/formatters';
import * as XLSX from 'xlsx';

const STATUS_OPTIONS = ['Ativo', 'Alta', 'Hospitalizado', 'Cancelado'];

export default function PatientManagement() {
  const [pacientes, setPacientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [editingPatient, setEditingPatient] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importStats, setImportStats] = useState(null);

  const [form, setForm] = useState({
    nome: '',
    dataNascimento: '',
    tipo: 'HB',
    numeroAtendimento: '',
    status: 'Ativo',
    cooperativas: []
  });

  const [listaCooperativas, setListaCooperativas] = useState([
    "ACR SERVIÇOS", "ACTUAL", "COOPERSUL", "EXATAFISIO", "FISIO RIO", "FONOPREST", "INOVA", "MODERNA", "QUERUBIM", "REVIGORE LIFE", "PREVENTE CARE", "VITALLE SAÚDE"
  ]);

  useEffect(() => {
    const unsub = onSnapshot(query(collection(db, getCollectionName('escalas_pacientes')), orderBy('nome')), snap => {
      setPacientes(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, err => {
      console.error("Erro ao buscar pacientes:", err);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const filteredPacientes = useMemo(() => {
    return pacientes.filter(p => 
      (p.nome || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.numeroAtendimento || '').toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [pacientes, searchTerm]);

  // Cálculos de KPIs
  const kpis = useMemo(() => {
    const total = pacientes.length;
    const countHB = pacientes.filter(p => p.tipo === 'HB').length;
    const countHS = pacientes.filter(p => p.tipo === 'HS').length;
    const countVinculados = pacientes.filter(p => Array.isArray(p.cooperativas) && p.cooperativas.length > 0).length;
    const countSemVinculo = total - countVinculados;

    // Vinculos por Cooperativa
    const coopMap = {};
    let totalLinks = 0;
    pacientes.forEach(p => {
      const v = Array.isArray(p.cooperativas) ? p.cooperativas : [];
      totalLinks += v.length;
      v.forEach(c => {
        coopMap[c] = (coopMap[c] || 0) + 1;
      });
    });

    return { total, countHB, countHS, countVinculados, countSemVinculo, totalLinks, coopMap };
  }, [pacientes]);

  const handleOpenModal = (p = null) => {
    if (p) {
      setEditingPatient(p);
      setForm({
        nome: p.nome || '',
        dataNascimento: p.dataNascimento || '',
        tipo: p.tipo || 'HB',
        numeroAtendimento: p.numeroAtendimento || '',
        status: p.status || 'Ativo',
        cooperativas: p.cooperativas || []
      });
    } else {
      setEditingPatient(null);
      setForm({
        nome: '',
        dataNascimento: '',
        tipo: 'HB',
        numeroAtendimento: '',
        status: 'Ativo',
        cooperativas: []
      });
    }
    setIsModalOpen(true);
  };

  const handleToggleCoopVinculo = (coop) => {
    setForm(prev => {
      const coops = prev.cooperativas || [];
      if (coops.includes(coop)) {
        return { ...prev, cooperativas: coops.filter(c => c !== coop) };
      } else {
        return { ...prev, cooperativas: [...coops, coop] };
      }
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const dataToSave = {
        ...form,
        nome: toTitleCase(form.nome)
      };

      if (editingPatient) {
        await updateDoc(doc(db, getCollectionName('escalas_pacientes'), editingPatient.id), dataToSave);
      } else {
        await addDoc(collection(db, getCollectionName('escalas_pacientes')), {
          ...dataToSave,
          criadoEm: new Date().toISOString(),
          criadoPor: auth.currentUser?.email
        });
      }
      setIsModalOpen(false);
    } catch (err) {
      console.error(err);
      alert("Erro ao salvar paciente.");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Deseja realmente excluir este paciente? Isso pode afetar escalas existentes.")) {
      try {
        await deleteDoc(doc(db, getCollectionName('escalas_pacientes'), id));
      } catch (err) {
        console.error(err);
        alert("Erro ao excluir paciente.");
      }
    }
  };

  const handleImportExcel = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImporting(true);
    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        const data = evt.target.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const json = XLSX.utils.sheet_to_json(sheet);

        let created = 0;
        let updated = 0;
        let skipped = 0;

        const currentSnap = await getDocs(collection(db, getCollectionName('escalas_pacientes')));
        const currentPacientes = currentSnap.docs.map(d => ({ id: d.id, ...d.data() }));

        const batch = writeBatch(db);
        let batchCount = 0;

        for (const row of json) {
          const nome = row['Nome'] || row['NOME'] || row['Paciente'];
          const nascRaw = row['Data de Nascimento'] || row['Nascimento'] || row['DATA NASCIMENTO'];
          const filialRaw = row['Filial'] || row['Unidade'] || row['FILIAL'];
          const atendimentoRaw = row['Atendimento'] || row['Nº Atendimento'] || row['ATENDIMENTO'] || row['Numero Atendimento'];
          
          if (!nome) continue;

          // Conversão de data do Excel
          let nasc = '';
          if (nascRaw) {
             if (typeof nascRaw === 'number') {
                const date = new Date((nascRaw - 25569) * 86400 * 1000);
                nasc = date.toISOString().split('T')[0];
             } else if (typeof nascRaw === 'string') {
                const parts = nascRaw.split('/');
                if (parts.length === 3) {
                   nasc = `${parts[2]}-${parts[1].padStart(2,'0')}-${parts[0].padStart(2,'0')}`;
                } else {
                   nasc = nascRaw;
                }
             }
          }

          const tipo = (filialRaw?.toString().toUpperCase().includes('SENIOR') || filialRaw?.toString().toUpperCase().includes('HS')) ? 'HS' : 'HB';
          const numAtendimento = atendimentoRaw?.toString().trim() || '';

          // Match Logic: Nome + Data Nascimento (se disponível)
          const existing = currentPacientes.find(p => 
            p.nome?.trim().toLowerCase() === nome.toString().trim().toLowerCase() && 
            (p.dataNascimento === nasc || !nasc || !p.dataNascimento)
          );

          if (existing) {
            // Unificar: Atualizar campos se houver novidade, mas manter o resto intacto
            const updates = {};
            if (tipo && existing.tipo !== tipo) updates.tipo = tipo;
            if (numAtendimento && existing.numeroAtendimento !== numAtendimento) updates.numeroAtendimento = numAtendimento;
            
            if (Object.keys(updates).length > 0) {
              batch.update(doc(db, getCollectionName('escalas_pacientes'), existing.id), {
                ...updates,
                atualizadoEmImport: new Date().toISOString()
              });
              updated++;
              batchCount++;
            } else {
              skipped++;
            }
          } else {
            const newRef = doc(collection(db, getCollectionName('escalas_pacientes')));
            batch.set(newRef, {
              nome: nome.toString().trim(),
              dataNascimento: nasc || '',
              tipo: tipo,
              numeroAtendimento: numAtendimento,
              status: 'Ativo',
              cooperativas: [],
              criadoEm: new Date().toISOString(),
              importado: true
            });
            created++;
            batchCount++;
          }

          if (batchCount >= 400) {
            await batch.commit();
            batchCount = 0;
          }
        }

        if (batchCount > 0) await batch.commit();

        setImportStats({ created, updated, skipped });
        setImporting(false);
      } catch (err) {
        console.error(err);
        alert("Erro ao processar arquivo: " + err.message);
        setImporting(false);
      }
    };
    reader.readAsBinaryString(file);
  };

  const s = {
    container: { padding: '30px', background: '#f8fafc', minHeight: '100vh', fontFamily: "'Inter', sans-serif" },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px', background: '#fff', padding: '24px 32px', borderRadius: '20px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', border: '1px solid #e2e8f0' },
    title: { margin: 0, fontSize: '1.6rem', fontWeight: 800, color: '#0f172a', display: 'flex', alignItems: 'center', gap: '12px' },
    actions: { display: 'flex', gap: '12px' },
    btn: { padding: '12px 24px', borderRadius: '12px', border: 'none', fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.9rem' },
    btnPrimary: { background: '#00897b', color: '#fff', boxShadow: '0 4px 6px -1px rgba(0, 137, 123, 0.2)' },
    btnSecondary: { background: '#fff', color: '#475569', border: '1px solid #e2e8f0' },
    searchBar: { position: 'relative', marginBottom: '24px' },
    searchInput: { width: '100%', padding: '16px 20px 16px 52px', borderRadius: '16px', border: '1px solid #e2e8f0', background: '#fff', fontSize: '1rem', outline: 'none', transition: 'border-color 0.2s', boxShadow: '0 2px 4px rgba(0,0,0,0.02)' },
    tableCard: { background: '#fff', borderRadius: '20px', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.05)', overflow: 'hidden', border: '1px solid #e2e8f0' },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { textAlign: 'left', padding: '18px 24px', background: '#f8fafc', borderBottom: '2px solid #f1f5f9', color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.05em' },
    td: { padding: '18px 24px', borderBottom: '1px solid #f1f5f9', color: '#334155', fontSize: '0.95rem' },
    badge: (status) => ({
      padding: '6px 12px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 800, textTransform: 'uppercase',
      background: status === 'Ativo' ? '#dcfce7' : status === 'Alta' ? '#f1f5f9' : status === 'Hospitalizado' ? '#fee2e2' : '#fef9c3',
      color: status === 'Ativo' ? '#166534' : status === 'Alta' ? '#475569' : status === 'Hospitalizado' ? '#991b1b' : '#854d0e'
    }),
    filialBadge: (tipo) => ({
      padding: '4px 10px', borderRadius: '8px', fontSize: '0.8rem', fontWeight: 700,
      background: tipo === 'HB' ? '#fdf2f8' : '#eff6ff',
      color: tipo === 'HB' ? '#be185d' : '#1d4ed8',
      border: `1px solid ${tipo === 'HB' ? '#fbcfe8' : '#dbeafe'}`,
      display: 'inline-flex', alignItems: 'center', gap: '6px'
    }),
    modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 },
    modal: { background: '#fff', width: '550px', borderRadius: '28px', padding: '40px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.3)', position: 'relative', maxHeight: '90vh', overflowY: 'auto' },
    formGroup: { marginBottom: '24px' },
    label: { display: 'block', marginBottom: '8px', fontWeight: 700, color: '#1e293b', fontSize: '0.9rem' },
    input: { width: '100%', padding: '14px 18px', borderRadius: '12px', border: '2px solid #f1f5f9', outline: 'none', fontSize: '1rem', transition: 'border-color 0.2s', background: '#f8fafc' },
    
    // KPI Styles
    kpiContainer: { display: 'flex', gap: '20px', marginBottom: '30px', flexWrap: 'wrap' },
    kpiCard: { flex: 1, minWidth: '240px', background: '#fff', padding: '24px', borderRadius: '20px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' },
    kpiValue: { fontSize: '2.2rem', fontWeight: 800, color: '#0f172a', margin: '8px 0' },
    kpiLabel: { fontSize: '0.85rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' },
    kpiSub: { fontSize: '0.8rem', color: '#94a3b8', marginTop: '4px' }
  };

  return (
    <div style={s.container}>
      <div style={s.header}>
        <h1 style={s.title}><span>👥</span> Gestão de Pacientes</h1>
        <div style={s.actions}>
          <button style={{...s.btn, ...s.btnSecondary}} onClick={() => setIsImportModalOpen(true)}>📤 Importar Excel</button>
          <button style={{...s.btn, ...s.btnPrimary}} onClick={() => handleOpenModal()}>+ Adicionar Paciente</button>
        </div>
      </div>

      <div style={s.kpiContainer}>
        <div style={s.kpiCard}>
          <div style={s.kpiLabel}>Total de Pacientes</div>
          <div style={s.kpiValue}>{kpis.total}</div>
          <div style={s.kpiSub}>
            <span style={{color: '#be185d'}}>👶 {kpis.countHB} HB</span> • <span style={{color: '#1d4ed8'}}>👴 {kpis.countHS} HS</span>
          </div>
        </div>

        <div style={s.kpiCard}>
          <div style={s.kpiLabel}>Pacientes Vinculados</div>
          <div style={{...s.kpiValue, color: '#00897b'}}>{kpis.countVinculados}</div>
          <div style={s.kpiSub}>
            <span style={{color: '#ea580c'}}>⚠️ {kpis.countSemVinculo} sem vínculo</span>
          </div>
        </div>

        <div style={{...s.kpiCard, flex: 2}}>
          <div style={s.kpiLabel}>Vínculos por Cooperativa</div>
          <div style={{ marginTop: '12px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '8px', maxHeight: '100px', overflowY: 'auto', paddingRight: '10px' }}>
            {Object.entries(kpis.coopMap).sort((a,b) => b[1] - a[1]).map(([name, count]) => (
              <div key={name} style={{ background: '#f8fafc', padding: '6px 10px', borderRadius: '8px', fontSize: '0.75rem', display: 'flex', justifyContent: 'space-between', border: '1px solid #e2e8f0' }}>
                <span style={{ fontWeight: 600, color: '#475569', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
                <span style={{ fontWeight: 800, color: '#0f172a' }}>{count}</span>
              </div>
            ))}
            {Object.keys(kpis.coopMap).length === 0 && <div style={{color: '#94a3b8', fontSize: '0.8rem'}}>Nenhum vínculo ativo</div>}
          </div>
        </div>
      </div>

      <div style={s.searchBar}>
        <input 
          style={s.searchInput} 
          placeholder="Buscar por nome ou número de atendimento..." 
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
        />
        <span style={{ position: 'absolute', left: '20px', top: '50%', transform: 'translateY(-50%)', fontSize: '1.2rem', opacity: 0.4 }}>🔍</span>
      </div>

      <div style={s.tableCard}>
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>Nome do Paciente</th>
              <th style={s.th}>Data de Nascimento</th>
              <th style={s.th}>Nº Atendimento</th>
              <th style={s.th}>Filial</th>
              <th style={s.th}>Status</th>
              <th style={{...s.th, textAlign: 'center'}}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={{ textAlign: 'center', padding: '60px', color: '#64748b' }}>Carregando dados dos pacientes...</td></tr>
            ) : filteredPacientes.length === 0 ? (
              <tr><td colSpan={6} style={{ textAlign: 'center', padding: '60px', color: '#64748b' }}>Nenhum paciente encontrado.</td></tr>
            ) : filteredPacientes.map(p => (
              <tr key={p.id} style={{ transition: 'background 0.2s' }}>
                <td style={{...s.td, fontWeight: 700, color: '#0f172a'}}>{p.nome}</td>
                <td style={s.td}>{p.dataNascimento ? new Date(p.dataNascimento + 'T00:00:00').toLocaleDateString('pt-BR') : '-'}</td>
                <td style={s.td}><code style={{ background: '#f1f5f9', padding: '4px 8px', borderRadius: '6px', fontSize: '0.85rem' }}>{p.numeroAtendimento || '-'}</code></td>
                <td style={s.td}>
                  <span style={s.filialBadge(p.tipo)}>
                    {p.tipo === 'HB' ? '👶 Homebaby' : '👴 Senior'}
                  </span>
                </td>
                <td style={s.td}><span style={s.badge(p.status || 'Ativo')}>{p.status || 'Ativo'}</span></td>
                <td style={{...s.td, textAlign: 'center'}}>
                  <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                    <button style={{ background: '#f1f5f9', border: 'none', cursor: 'pointer', color: '#00897b', padding: '8px', borderRadius: '8px' }} onClick={() => handleOpenModal(p)} title="Editar">✏️</button>
                    <button style={{ background: '#fff1f2', border: 'none', cursor: 'pointer', color: '#ef4444', padding: '8px', borderRadius: '8px' }} onClick={() => handleDelete(p.id)} title="Excluir">🗑️</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal CRUD */}
      {isModalOpen && (
        <div style={s.modalOverlay}>
          <form style={s.modal} onSubmit={handleSave}>
            <h2 style={{ marginBottom: '30px', fontSize: '1.4rem', fontWeight: 800 }}>{editingPatient ? '📝 Editar Paciente' : '👤 Novo Paciente'}</h2>
            
            <div style={s.formGroup}>
              <label style={s.label}>Nome Completo</label>
              <input style={s.input} value={form.nome} onChange={e => setForm({...form, nome: e.target.value})} required placeholder="Ex: João da Silva" />
            </div>

            <div style={{ display: 'flex', gap: '20px' }}>
              <div style={{...s.formGroup, flex: 1}}>
                <label style={s.label}>Data de Nascimento</label>
                <input type="date" style={s.input} value={form.dataNascimento} onChange={e => setForm({...form, dataNascimento: e.target.value})} />
              </div>
              <div style={{...s.formGroup, flex: 1}}>
                <label style={s.label}>Nº Atendimento</label>
                <input style={s.input} value={form.numeroAtendimento} onChange={e => setForm({...form, numeroAtendimento: e.target.value})} placeholder="000000" />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '20px' }}>
              <div style={{...s.formGroup, flex: 1}}>
                <label style={s.label}>Filial</label>
                <select 
                  style={{...s.input, ...s.filialBadge(form.tipo), width: '100%', height: '48px'}} 
                  value={form.tipo} 
                  onChange={e => setForm({...form, tipo: e.target.value})}
                >
                  <option value="HB">👶 Homebaby (HB)</option>
                  <option value="HS">👴 Vitalcare (HS)</option>
                </select>
              </div>
              <div style={{...s.formGroup, flex: 1}}>
                <label style={s.label}>Status Atual</label>
                <select style={s.input} value={form.status} onChange={e => setForm({...form, status: e.target.value})}>
                  {STATUS_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
              </div>
            </div>

            <label style={s.label}>Cooperativas Vinculadas</label>
            <div style={{ 
              display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', 
              background: '#f8fafc', padding: '16px', borderRadius: '16px', 
              border: '2px solid #f1f5f9', maxHeight: '120px', overflowY: 'auto',
              marginBottom: '30px'
            }}>
              {listaCooperativas.map(coop => (
                <label key={coop} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.8rem', cursor: 'pointer', color: '#475569', fontWeight: 600 }}>
                  <input 
                    type="checkbox" 
                    checked={(form.cooperativas || []).includes(coop)}
                    onChange={() => handleToggleCoopVinculo(coop)}
                  />
                  {coop}
                </label>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button type="button" style={{...s.btn, ...s.btnSecondary}} onClick={() => setIsModalOpen(false)}>Cancelar</button>
              <button type="submit" style={{...s.btn, ...s.btnPrimary}}>Salvar Cadastro</button>
            </div>
          </form>
        </div>
      )}

      {/* Modal Import */}
      {isImportModalOpen && (
        <div style={s.modalOverlay}>
          <div style={s.modal}>
            <h2 style={{ marginBottom: '15px', fontSize: '1.4rem', fontWeight: 800 }}>📤 Importar via Excel</h2>
            <p style={{ color: '#64748b', fontSize: '0.95rem', lineHeight: '1.6', marginBottom: '24px' }}>
              Suba sua planilha de pacientes. O sistema irá ignorar cadastros com o mesmo Nome e Data de Nascimento para evitar duplicidade. <br/>
              <span style={{ color: '#00897b', fontWeight: 600 }}>Colunas: Nome, Data de Nascimento, Filial.</span>
            </p>

            <div style={{ padding: '30px', border: '2px dashed #e2e8f0', borderRadius: '20px', textAlign: 'center', background: '#f8fafc' }}>
              <input 
                type="file" 
                id="excel-upload"
                accept=".xlsx, .xls" 
                onChange={handleImportExcel} 
                disabled={importing}
                style={{ display: 'none' }}
              />
              <label htmlFor="excel-upload" style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '2rem' }}>📊</span>
                <span style={{ fontWeight: 700, color: '#475569' }}>{importing ? 'Processando arquivo...' : 'Clique para selecionar o arquivo'}</span>
              </label>
            </div>

            {importStats && (
              <div style={{ marginTop: '24px', padding: '20px', background: '#f0fdf4', borderRadius: '16px', border: '1px solid #dcfce7' }}>
                <p style={{ margin: '0 0 8px', color: '#166534', fontWeight: 700 }}>✅ Importação Concluída!</p>
                <p style={{ margin: 0, fontSize: '0.9rem', color: '#15803d' }}>
                  Novos pacientes: <strong>{importStats.created}</strong><br/>
                  Pacientes atualizados: <strong>{importStats.updated}</strong><br/>
                  Ignorados (Sem alteração): <strong>{importStats.skipped}</strong>
                </p>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '30px' }}>
              <button style={{...s.btn, ...s.btnSecondary, width: '100%'}} onClick={() => { setIsImportModalOpen(false); setImportStats(null); }}>Fechar Janela</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
